import { useEffect, type ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntApp, Layout, Spin } from 'antd';
import enUS from 'antd/locale/en_US';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/en';
import { useTranslation } from 'react-i18next';
import { AuthGuard, useSupabaseSession, parseUserRole } from '@ubus/shared';

import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Vehicles from './pages/Vehicles';
import Drivers from './pages/Drivers';
import Trips from './pages/Trips';
import TripMap from './pages/TripMap';
import SOSEvents from './pages/SOSEvents';
import Organizations from './pages/Organizations';
import DataManagement from './pages/DataManagement';
import Settings from './pages/Settings';
import AccountSecurity from './pages/AccountSecurity';
import VehicleInspections from './pages/VehicleInspections';
import FleetOperators from './pages/FleetOperators';
import FleetAvailability from './pages/FleetAvailability';
import Jobs from './pages/Jobs';
import JobFormPage from './pages/JobFormPage';
import JobDetailPage from './pages/JobDetailPage';
import DailySchedule from './pages/DailySchedule';
import ScheduleOverviewGate from './components/ScheduleOverviewGate';
import OwnerOrganizations from './pages/OwnerOrganizations';
import ForbiddenPage from './pages/ForbiddenPage';
import NotFoundPage from './pages/NotFoundPage';
import ReminderSettings from './pages/ReminderSettings';
import ReminderRecords from './pages/ReminderRecords';
import SystemConfig from './pages/SystemConfig';
import Roles from './pages/Roles';
import Users from './pages/Users';
import TeamMembers from './pages/TeamMembers';
import FleetProfile from './pages/FleetProfile';
import AdminLayout from './components/AdminLayout';
import PermissionRoute from './components/PermissionRoute';
import HomeRedirect from './components/HomeRedirect';
import { adminTheme } from './theme/adminTheme';
import { homePathForRole } from './utils/homePath';
import { DisplayTimezoneProvider } from './components/DisplayTimezoneProvider';

import './index.css';

function AuthLoadingFallback() {
  const { t } = useTranslation();
  return (
    <Layout style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Spin size="large" tip={t('common.verifyingAuth')} />
    </Layout>
  );
}

function AppProviders({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh-CN';

  useEffect(() => {
    dayjs.locale(isZh ? 'zh-cn' : 'en');
  }, [isZh]);

  return (
    <ConfigProvider locale={isZh ? zhCN : enUS} theme={adminTheme}>
      <DisplayTimezoneProvider>
        <AntApp>{children}</AntApp>
      </DisplayTimezoneProvider>
    </ConfigProvider>
  );
}

function App() {
  const { session, loading } = useSupabaseSession();
  const { t } = useTranslation();

  if (loading) {
    return (
      <AppProviders>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Spin size="large" tip={t('common.loading')} />
        </div>
      </AppProviders>
    );
  }

  return (
    <AppProviders>
      <Routes>
        <Route
          path="/login"
          element={
            session ? (
              <Navigate to={homePathForRole(parseUserRole(session.user))} replace />
            ) : (
              <Login />
            )
          }
        />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route
          path="/"
          element={
            <AuthGuard requiredRole={['admin', 'fleet_owner']} loadingFallback={<AuthLoadingFallback />}>
              <AdminLayout />
            </AuthGuard>
          }
        >
          <Route index element={<HomeRedirect />} />
          <Route
            path="dashboard"
            element={
              <PermissionRoute allowIdentity={['admin', 'fleet_owner']}>
                <Dashboard />
              </PermissionRoute>
            }
          />
          <Route
            path="vehicles"
            element={
              <PermissionRoute allowIdentity={['admin', 'fleet_owner']}>
                <Vehicles />
              </PermissionRoute>
            }
          />
          <Route
            path="drivers"
            element={
              <PermissionRoute allowIdentity={['admin', 'fleet_owner']}>
                <Drivers />
              </PermissionRoute>
            }
          />

          <Route
            path="trips"
            element={
              <PermissionRoute allowIdentity={['admin', 'fleet_owner']}>
                <Trips />
              </PermissionRoute>
            }
          />
          <Route
            path="trips/:id/map"
            element={
              <PermissionRoute allowIdentity={['admin', 'fleet_owner']} permission="trips.view">
                <TripMap />
              </PermissionRoute>
            }
          />
          <Route path="dispatch" element={<Navigate to="/jobs" replace />} />
          <Route
            path="sos-events"
            element={
              <PermissionRoute allowIdentity={['admin', 'fleet_owner']} permission="sos.view">
                <SOSEvents />
              </PermissionRoute>
            }
          />
          <Route
            path="organizations"
            element={
              <PermissionRoute allowIdentity={['admin']}>
                <Organizations />
              </PermissionRoute>
            }
          />
          <Route
            path="fleet-operators"
            element={
              <PermissionRoute allowIdentity={['admin']}>
                <FleetOperators />
              </PermissionRoute>
            }
          />
          <Route
            path="fleet-availability"
            element={
              <PermissionRoute
                allowIdentity={['admin', 'fleet_owner']}
                permission="fleet_availability.view"
              >
                <FleetAvailability />
              </PermissionRoute>
            }
          />
          <Route
            path="jobs"
            element={
              <PermissionRoute allowIdentity={['admin', 'fleet_owner']}>
                <Jobs />
              </PermissionRoute>
            }
          />
          <Route
            path="jobs/new"
            element={
              <PermissionRoute allowIdentity={['admin', 'fleet_owner']}>
                <JobFormPage />
              </PermissionRoute>
            }
          />
          <Route
            path="jobs/:id"
            element={
              <PermissionRoute allowIdentity={['admin', 'fleet_owner']}>
                <JobDetailPage />
              </PermissionRoute>
            }
          />
          <Route
            path="jobs/:id/edit"
            element={
              <PermissionRoute allowIdentity={['admin', 'fleet_owner']}>
                <JobFormPage />
              </PermissionRoute>
            }
          />
          <Route path="schedule-overview" element={<ScheduleOverviewGate />} />
          <Route
            path="daily-schedule"
            element={
              <PermissionRoute allowIdentity={['fleet_owner']} permission="daily_schedule.view">
                <DailySchedule />
              </PermissionRoute>
            }
          />
          <Route
            path="data-management"
            element={
              <PermissionRoute allowIdentity={['admin']}>
                <DataManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="vehicle-inspections"
            element={
              <PermissionRoute allowIdentity={['admin', 'fleet_owner']} permission="operations.view">
                <VehicleInspections />
              </PermissionRoute>
            }
          />
          <Route path="operations" element={<Navigate to="/vehicle-inspections" replace />} />
          <Route
            path="account-security"
            element={
              <PermissionRoute allowIdentity={['admin', 'fleet_owner']}>
                <AccountSecurity />
              </PermissionRoute>
            }
          />
          <Route
            path="reminder-records"
            element={
              <PermissionRoute allowIdentity={['admin', 'fleet_owner']}>
                <ReminderRecords />
              </PermissionRoute>
            }
          />
          <Route
            path="system-config"
            element={
              <PermissionRoute allowIdentity={['admin']} permission="system_config.view">
                <SystemConfig />
              </PermissionRoute>
            }
          />
          <Route
            path="reminder-settings"
            element={
              <PermissionRoute allowIdentity={['admin']}>
                <ReminderSettings />
              </PermissionRoute>
            }
          />
          <Route
            path="settings"
            element={
              <PermissionRoute allowIdentity={['admin']}>
                <Settings />
              </PermissionRoute>
            }
          />
          <Route
            path="roles"
            element={
              <PermissionRoute allowIdentity={['admin']}>
                <Roles />
              </PermissionRoute>
            }
          />
          <Route
            path="users"
            element={
              <PermissionRoute allowIdentity={['admin']}>
                <Users />
              </PermissionRoute>
            }
          />
          <Route
            path="team"
            element={
              <PermissionRoute allowIdentity={['fleet_owner']} permission="team.view">
                <TeamMembers />
              </PermissionRoute>
            }
          />
          <Route
            path="fleet-profile"
            element={
              <PermissionRoute allowIdentity={['fleet_owner']} permission="fleet_profile.view">
                <FleetProfile />
              </PermissionRoute>
            }
          />

          <Route path="owner-schedule" element={<Navigate to="/trips" replace />} />
          <Route path="owner-pending" element={<Navigate to="/jobs" replace />} />
          <Route
            path="owner-organizations"
            element={
              <PermissionRoute allowIdentity={['fleet_owner']}>
                <OwnerOrganizations />
              </PermissionRoute>
            }
          />

          <Route path="403" element={<ForbiddenPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        <Route path="*" element={session ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
      </Routes>
    </AppProviders>
  );
}

export default App;
